/**
 * Particles (art §4.14): one instanced draw of camera-facing quads; the fragment evaluates the shape SDF, fills with a
 * flat pigment + tempera noise and an optional ink outline; premultiplied output; the blend keeps the figure mask.
 * Shapes (`params.y`): 0 ink splat · 1 gold sparkle · 2 fur tuft · 3 dust cloud · 4 rubric burst · 5 clang star ·
 * 6 blot · 7 droplet · 8 weapon crescent · 9 ground impact ring · 10 parry shield · 11 hanging cut · 12 impact ray · 13 seal.
 */
import * as d from 'typegpu/data';
import * as std from 'typegpu/std';
import tgpu from 'typegpu';
import { frameLayout } from '../gpu/layouts';
import { sdStar, sdTaper, vnoise } from './lib';
import { P_INK, P_VELLUM } from './pigments';

const PV = { uv: d.vec2f, color: d.vec4f, params: d.vec4f };

export const particleVertex = tgpu.vertexFn({
  in: { vid: d.builtin.vertexIndex, posSize: d.vec4f, color: d.vec4f, params: d.vec4f },
  out: { position: d.builtin.position, ...PV },
})((input) => {
  'use gpu';
  const f = frameLayout.$.frame;
  const corners = [d.vec2f(-1, -1), d.vec2f(1, -1), d.vec2f(1, 1), d.vec2f(-1, -1), d.vec2f(1, 1), d.vec2f(-1, 1)];
  const c = corners[input.vid];
  const stretch = std.max(input.params.z, 1.0);
  const cs = std.cos(input.params.x);
  const sn = std.sin(input.params.x);
  const cc = d.vec2f(c.x * stretch, c.y);
  const r = d.vec2f(cc.x * cs - cc.y * sn, cc.x * sn + cc.y * cs);
  const viewPos = f.view.mul(d.vec4f(input.posSize.xyz, 1));
  let position=f.proj.mul(viewPos.add(d.vec4f(r.mul(input.posSize.w), 0, 0)));
  if(input.params.y>8.5&&input.params.y<9.5){
    // A landing wave belongs to the ground plane, not a floating camera-facing disc.
    const world=input.posSize.xyz.add(d.vec3f(c.x*input.posSize.w,0,c.y*input.posSize.w));
    position=f.proj.mul(f.view.mul(d.vec4f(world,1)));
  }
  return {
    position,
    uv: d.vec2f(c.x * stretch, c.y),
    color: input.color,
    params: input.params,
  };
});

export const particleFragment = tgpu.fragmentFn({
  in: PV,
  out: d.vec4f,
})((input) => {
  'use gpu';
  // Perspective interpolation can turn an integer-valued float into 10.999999.
  // Truncation then selects the preceding SDF (a shield instead of a cut).
  const shape = d.u32(std.round(input.params.y));
  const seed = input.params.w;
  const st = std.max(input.params.z, 1.0);
  const p = input.uv;
  const aa = std.max(std.fwidth(p.y) * 0.9, 0.01);
  let dd = d.f32(1);
  let outline = d.f32(0);
  if (shape === 0 || shape === 7) {
    // ink drop: a round head leading along the velocity with a tapered tail behind (art §4.14 «Клякса»)
    const head = d.vec2f(st - 0.62, 0.0);
    const r0 = 0.55 + 0.06 * (seed - 0.5);
    const dh = std.length(p.sub(head)) - r0;
    const dt = sdTaper(p, d.vec2f(-st + 0.08, 0.04 * (seed - 0.5)), head, 0.03, r0 * 0.8);
    dd = std.min(dh, dt);
    // a satellite droplet on bigger drops
    const sat = std.length(p.sub(d.vec2f(head.x - 0.2, 0.75 * (seed - 0.5) + std.sign(seed - 0.5) * 0.55))) - 0.14;
    dd = std.min(dd, std.select(d.f32(9), sat, seed > 0.65));
    if (shape === 7) { dd = std.length(p) - 0.7; }
  } else if (shape === 1) {
    dd = sdStar(p, d.f32(4), 0.12, 0.95);
    outline = 0.0;
  } else if (shape === 2) {
    // fur tuft: three tapered arcs
    const t1 = sdTaper(p, d.vec2f(-0.7, -0.5), d.vec2f(0.6, 0.55), 0.14, 0.02);
    const t2 = sdTaper(p, d.vec2f(-0.5, -0.65), d.vec2f(0.75, 0.15), 0.12, 0.02);
    const t3 = sdTaper(p, d.vec2f(-0.6, -0.35), d.vec2f(0.3, 0.75), 0.1, 0.02);
    dd = std.min(std.min(t1, t2), t3);
    outline = 1.0;
  } else if (shape === 3) {
    const ang = std.atan2(p.y, p.x);
    dd = std.length(p) - 0.8 * (1.0 + 0.12 * std.sin(8.0 * ang + seed * 20.0));
    outline = 1.0;
  } else if (shape === 4) {
    const ang = std.atan2(p.y, p.x);
    const ray = std.abs(std.fract(ang / 0.785398 + 0.5) - 0.5) * 0.785398 * std.length(p);
    dd = std.max(ray - 0.035, std.length(p) - 0.98);
    dd = std.max(dd, 0.25 - std.length(p));
  } else if (shape === 5) {
    dd = sdStar(p, d.f32(8), 0.1, 0.95);
    outline = 1.0;
  } else if(shape===8){
    const radius=std.length(p);
    const sweep=std.smoothstep(-.45,.75,p.x);
    const width=.012+.075*sweep;
    dd=std.max(std.abs(radius-.74)-width,-p.y-.38);
    // A second hairline carries a dry-brush echo behind the blade.
    const echo=std.max(std.abs(radius-.57)-.012,-p.y+.02);
    dd=std.min(dd,echo);
  } else if(shape===9){
    const radius=std.length(p);
    const angle=std.atan2(p.y,p.x);
    const outer=std.abs(radius-.79)-.036;
    const broken=std.max(std.abs(radius-.57)-.017,std.sin(angle*12+seed*3)*.05);
    dd=std.min(outer,broken);
  } else if(shape===10){
    // A pointed enamel ward, deliberately unlike an attacking crescent.
    const shield=std.max(std.abs(p.x)*.82+std.max(0.0,-p.y)*.5-.62,std.abs(p.y)-.78);
    const rim=std.abs(shield)-.045;
    const cross=std.min(std.max(std.abs(p.x)-.035,std.abs(p.y-.08)-.4),std.max(std.abs(p.x)-.28,std.abs(p.y-.2)-.035));
    dd=std.min(rim,cross);
  } else if(shape===11){
    // Reference's almost-straight hanging cut, sharp at both ends.
    const width=.012+.045*std.pow(std.max(0.0,1-p.x*p.x),.7);
    dd=std.max(std.abs(p.y-(1-p.x*p.x)*.04)-width,std.abs(p.x)-.97);
    dd=std.max(dd,p.x-(input.params.w*2-1));
  } else if(shape===12){
    const width=.015+.1*std.pow(std.max(0.0,1-p.x*p.x),2);
    dd=std.max(std.abs(p.y)-width,std.abs(p.x)-.95);
  } else if(shape===13){
    // One complete inked, enamel-green stamp. Its hollow field leaves faces
    // visible; the thick hexagon and forked staff survive phone resolution.
    const hex=std.max(std.abs(p.x)*.8660254+std.abs(p.y)*.5,std.abs(p.y))-.72;
    const rim=std.abs(hex)-.045;
    const stem=std.max(std.abs(p.x)-.038,std.abs(p.y+.035)-.36);
    const left=sdTaper(p,d.vec2f(0,.05),d.vec2f(-.23,.29),.034,.043);
    const right=sdTaper(p,d.vec2f(0,.05),d.vec2f(.23,.29),.034,.043);
    const foot=std.max(std.abs(p.x)-.17,std.abs(p.y+.31)-.03);
    dd=std.min(rim,std.min(stem,std.min(foot,std.min(left,right))));
  } else {
    const ang = std.atan2(p.y, p.x);
    dd = std.length(p) - 0.75 * (1.0 + 0.2 * (vnoise(d.vec2f(ang * 2.0, seed * 9.0)) - 0.5));
  }
  if(shape===8||shape===11){
    // Broken quill edges keep the stroke in the same medium as the miniature.
    dd+=(vnoise(p.mul(d.vec2f(37,13)).add(seed*7))-.5)*.018;
    outline=.45;
  }
  const a = 1.0 - std.smoothstep(-aa, aa, dd-std.select(0.0,.035,shape===13));
  let col = input.color.xyz.mul(1.0 + (vnoise(p.mul(2.0).add(d.vec2f(seed * 31.0))) - 0.5) * 0.1);
  if(shape===8)col=std.mix(col.mul(.78),P_VELLUM.$,std.smoothstep(.7,.77,std.length(p))*.32);
  if(shape===11)col=std.mix(col,P_VELLUM.$,(1-std.smoothstep(.005,.035,std.abs(p.y-(1-p.x*p.x)*.04)))*.38);
  const ring = (1.0 - std.smoothstep(0.0, aa * 2.0, std.abs(dd + aa * 1.5))) * outline;
  col = std.mix(col, P_INK.$, ring);
  if(shape===13){
    // Vellum edging separates the stamp from lapis; a dry ink keyline keeps
    // it equally clear over cloth and pale stone. No emissive/bloom layer.
    col=std.mix(input.color.xyz,P_VELLUM.$,.26);
    col=std.mix(col,P_INK.$,std.smoothstep(-.006,.008,dd));
    col=std.mix(col,P_VELLUM.$,std.smoothstep(.018,.03,dd));
  }
  const alpha = a * input.color.w;
  if (alpha < 0.004) {
    std.discard();
  }
  return d.vec4f(col.mul(alpha), alpha);
});
